import API from './api';
import { handleApiError } from './utilsService';

// Servicio para la gestión de archivos
const fileService = {
  // Subir un archivo
  uploadFile: async (file, type = 'image', folder = 'uploads') => {
    try {
      // Validar el archivo
      if (!file) {
        throw new Error('No se ha proporcionado ningún archivo');
      }

      // Validar el tipo de archivo
      const allowedTypes = {
        image: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
        document: [
          'application/pdf',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        ],
        any: [],
      };

      const maxSizes = {
        image: 5 * 1024 * 1024, // 5MB
        document: 10 * 1024 * 1024, // 10MB
        any: 20 * 1024 * 1024, // 20MB
      };

      // Si el tipo no está definido, usar 'any'
      const fileType = type in allowedTypes ? type : 'any';

      // Validar el tipo de archivo si hay tipos permitidos
      if (allowedTypes[fileType].length > 0 && !allowedTypes[fileType].includes(file.type)) {
        throw new Error(
          `Tipo de archivo no permitido. Los tipos permitidos son: ${allowedTypes[fileType].join(', ')}`,
        );
      }

      // Validar el tamaño del archivo
      if (file.size > maxSizes[fileType]) {
        const maxSizeMB = maxSizes[fileType] / (1024 * 1024);
        throw new Error(
          `El archivo es demasiado grande. El tamaño máximo permitido es ${maxSizeMB}MB`,
        );
      }

      // Crear un objeto FormData para enviar el archivo
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', folder);

      // Enviar el archivo al servidor
      const response = await API.post('/files/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Error al subir el archivo');
    }
  },

  // Subir una imagen
  uploadImage: (imageFile, folder = 'images') => {
    return fileService.uploadFile(imageFile, 'image', folder);
  },

  // Subir una imagen de perfil
  uploadProfileImage: (imageFile) => {
    return fileService.uploadFile(imageFile, 'image', 'profiles');
  },

  // Eliminar un archivo
  deleteFile: async (fileUrl) => {
    try {
      const response = await API.delete('/files', { data: { fileUrl } });
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Error al eliminar el archivo');
    }
  },

  // Obtener la URL de un archivo
  getFileUrl: (fileName, folder = 'uploads') => {
    if (!fileName) return null;

    // Si la URL ya es completa, devolverla tal cual
    if (fileName.startsWith('http://') || fileName.startsWith('https://')) {
      return fileName;
    }

    // Construir la URL del archivo
    return `${process.env.REACT_APP_API_URL || ''}/files/${folder}/${fileName}`;
  },

  // Convertir un archivo a Base64
  fileToBase64: (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  },

  // Validar un archivo
  validateFile: (file, type = 'image') => {
    if (!file) {
      return { valid: false, error: 'No se ha proporcionado ningún archivo' };
    }

    const allowedTypes = {
      image: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      document: [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ],
      any: [],
    };

    const maxSizes = {
      image: 5 * 1024 * 1024, // 5MB
      document: 10 * 1024 * 1024, // 10MB
      any: 20 * 1024 * 1024, // 20MB
    };

    // Si el tipo no está definido, usar 'any'
    const fileType = type in allowedTypes ? type : 'any';

    // Validar el tipo de archivo si hay tipos permitidos
    if (allowedTypes[fileType].length > 0 && !allowedTypes[fileType].includes(file.type)) {
      return {
        valid: false,
        error: `Tipo de archivo no permitido. Los tipos permitidos son: ${allowedTypes[fileType].join(', ')}`,
      };
    }

    // Validar el tamaño del archivo
    if (file.size > maxSizes[fileType]) {
      const maxSizeMB = maxSizes[fileType] / (1024 * 1024);
      return {
        valid: false,
        error: `El archivo es demasiado grande. El tamaño máximo permitido es ${maxSizeMB}MB`,
      };
    }

    return { valid: true };
  },
};

export default fileService;
